import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import {
    WORKFLOW_DETAILS_REFERENCE_DATA_TYPES,
    WorkflowDetailsReferenceData
} from "@constants/reference-data";
import { LANGUAGE_SELECT_OPTIONS } from "@constants/language-select-options";
import { WORKFLOWS_DETAILS_URL, WORKFLOWS_URL } from "@constants/routes";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { ServiceEndpointTypes } from "@enums/service-endpoint-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { formatAdvancedWorkflowDetailsConfiguration } from "@formatters/advanced-workflow-details-configuration/advanced-workflow-details-configuration-formatter";
import { formatRpServiceConfiguration, formatRpServiceConfigurations } from "@formatters/rp-services/rp-service-configuration-formatter";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import {
    AdvancedWorkflowDetailsConfiguration,
    FormattedAdvancedWorkflowDetailsConfiguration,
    FormattedWorkflowDetailsConfiguration
} from "@idw-types/data/workflow-details-configuration";
import { FormattedRpServiceConfiguration } from "@idw-types/data/rp-service-configuration";
import { WorkflowChangeRequest } from "@idw-types/data/workflow-change-request";
import {
    setTableSettingsTableFilters,
    setTableSettingsTablePagination,
    setTableSettingsTableSortBy
} from "@redux/actions/table-settings-actions";
import { setAdvancedWorkflowDetails } from "@redux/actions/workflow-details-actions";
import { getMessagingTemplatesReduxRequest } from "@redux-requests/messaging-templates";
import {
    getAdvancedWorkflowDetailsReduxRequest,
    updateWorkflowDetailsReduxRequest
} from "@redux-requests/workflow-details";
import {
    getCommonReferenceDataReduxRequest
} from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { injectWorkflowId } from "@utils/parameter-injector";
import { navigateToUtil } from "@utils/navigation-util";
import { LoadingIndicator } from "../common/loading-indicator/loading-indicator";
import { CardContainer } from "../common/card-container/card-container";
import { ContentPageHeader } from "../common/content-page-header/content-page-header";
import { ContentPageBody } from "../common/content-page-body/content-page-body";
import { WorkflowDetailsComponent } from "./workflow-details-component/workflow-details-component";
import { SelectFormGroup } from "../common/select-form-group/select-form-group";
import styles from "./workflow-details-container.less";
import { ContentPageHeaderDocumentationLink } from "@containers/common/content-page-header-documentation-link/content-page-header-documentation-link";
import { CreateWorkflowVerificationServiceResponse } from "@requests/service-configuration/create-workflow-verification-service";
import { AxnReferenceDataTypes } from "@enums/axn-reference-data-types";
import { addNotDeployedWorkflowServicesToAxnRpServices } from "@utils/workflow-util";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";
import { getApAssertionStatusMaps } from "@utils/data-util";
import { ColumnFilter, ColumnSort } from "@tanstack/react-table";
import { getSessionState } from "@redux/selectors/root-selectors";
import { getWorkflowDetailsAdvancedWorkflowDetailsState } from "@redux/selectors/workflow-details-selectors";

export const WorkflowDetailsContainer = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const { workflowId } = useParams<{ workflowId: string }>();
    const [searchParams] = useSearchParams();

    const {
        isLoading: isLoadingAdvancedWorkflowDetails,
        advancedWorkflowDetails,
        shouldRefreshComponentState
    } = useSelector(getWorkflowDetailsAdvancedWorkflowDetailsState);
    const { session } = useSelector(getSessionState);
    const referenceData = useReferenceDataSelector<WorkflowDetailsReferenceData>(WORKFLOW_DETAILS_REFERENCE_DATA_TYPES, true);

    const [selectedLanguage, setSelectedLanguage] = useState<LocaleTypes>(LocaleTypes.ENGLISH);
    const [availableLanguages, setAvailableLanguages] = useState<Array<LocaleTypes>>([]);
    const [shouldLoadDetailsReferenceData, setShouldLoadDetailsReferenceData] = useState(true);
    const [shouldLoadWorkflowDetails, setShouldLoadWorkflowDetails] = useState(true);
    const [unformattedAdvancedWorkflowDetails, setUnformattedAdvancedWorkflowDetails] = useState<AdvancedWorkflowDetailsConfiguration>(null);
    const [workflowChangeRequests, setWorkflowChangeRequests] = useState<Array<WorkflowChangeRequest>>([]);
    const tableSettingsTableType = TableSettingsTableTypes.WORKFLOWS;

    const redirectToWorkflowsPage = () => {
        navigateToUtil(WORKFLOWS_URL, navigate, routerLocation.pathname);
    };

    const getUnformattedWorkflowDetails = async (showError: boolean, shouldRefreshDetailsReferenceData: boolean) => {
        const response = await getAdvancedWorkflowDetailsReduxRequest(dispatch)(workflowId, redirectToWorkflowsPage, showError);

        if (response) {
            setUnformattedAdvancedWorkflowDetails(response.advancedWorkflowDetails);
            setWorkflowChangeRequests(response.workflowChangeRequests);

            if (!referenceData.isLoading.workflowDetails && (shouldLoadDetailsReferenceData || shouldRefreshDetailsReferenceData)) {
                setShouldLoadDetailsReferenceData(false);
                getCommonReferenceDataReduxRequest(dispatch)(
                    WORKFLOW_DETAILS_REFERENCE_DATA_TYPES,
                    ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                    CommonReferenceDataPageTypes.WORKFLOW_DETAILS,
                    true,
                    ServiceEndpointTypes.OIDC,
                    response.advancedWorkflowDetails.workflow.relyingPartyID
                );
            }
        }
    };

    const getFormattedRpServiceForNewService = (data: CreateWorkflowVerificationServiceResponse): FormattedRpServiceConfiguration => {
        if (data) {
            const formattedRpServiceConfiguration = formatRpServiceConfiguration(
                data.axnAdminRPService,
                referenceData.axnIdentityProviders,
                referenceData.axnRelyingParties,
                referenceData.axnAvailablePropertyCategories,
                referenceData.axnServiceOfferings,
                referenceData.axnServiceOfferingDataSources,
                referenceData.fallbackServiceMappings,
                referenceData.axnAttributeTypes,
                referenceData.axnAttributeLabels,
                referenceData.attributeClassifications,
                referenceData.apAssertions,
                referenceData.axnAssertions,
                referenceData.deployedPolicyComponents,
                referenceData.axnAttributeTypeFields,
                referenceData.rpServiceAttributeProperties,
                referenceData.rpServiceAttributePropertyMaps,
                apAssertionStatusMaps,
                referenceData.assertionScoreServiceOfferings,
                referenceData.trustScoreServiceOfferings,
                referenceData.axnCompanies
            );

            const { workflow } = advancedWorkflowDetails;
            // add the new rp service into workflow rpservices
            const formattedRpService: FormattedRpServiceConfiguration = {
                ...formattedRpServiceConfiguration,
                workflowID: workflow.id,
                relyingParty: workflow.relyingParty
            };

            return formattedRpService;
        }

        return null;
    };

    useEffect(() => {
        let hasParams = false;
        if (searchParams.has("tableFilters")) {
            hasParams = true;
            try {
                const tableFilters = JSON.parse(searchParams.get("tableFilters")) as Array<ColumnFilter>;
                dispatch(setTableSettingsTableFilters(tableSettingsTableType, tableFilters, true));
            } catch (error) {
                console.error("Error parsing tableFilters from query params", error);
            }
        }
        if (searchParams.has("sortBy")) {
            hasParams = true;
            try {
                const sortBy = JSON.parse(searchParams.get("sortBy")) as Array<ColumnSort>;
                dispatch(setTableSettingsTableSortBy(tableSettingsTableType, sortBy, true));
            } catch (error) {
                console.error("Error parsing sortBy from query params", error)
            }
        }

        let pageSize;
        if (searchParams.has("pageSize")) {
            hasParams = true;
            try {
                pageSize = parseInt(searchParams.get("pageSize"));
            } catch (error) {
                console.error("Error parsing pageSize from query params", error)
            }
        }

        let pageIndex;
        if (searchParams.has("pageIndex")) {
            hasParams = true;
            try {
                pageIndex = parseInt(searchParams.get("pageIndex"));
            } catch (error) {
                console.error("Error parsing pageIndex from query params", error)
            }
        }

        let pagination = {};
        let shouldUpdatePagination = false;
        if (typeof pageSize !== "undefined") {
            pagination = { pageSize };
            shouldUpdatePagination = true;
        }
        if (typeof pageIndex !== "undefined") {
            pagination = { ...pagination, pageIndex };
            shouldUpdatePagination = true;
        }

        if (shouldUpdatePagination) {
            dispatch(setTableSettingsTablePagination(tableSettingsTableType, pagination, true));
        }

        if (hasParams) {
            const url = injectWorkflowId(WORKFLOWS_DETAILS_URL, workflowId);
            navigateToUtil(url, navigate, routerLocation.pathname);
        }
    }, []);

    useEffect(() => {
        if (!isLoadingAdvancedWorkflowDetails && shouldLoadWorkflowDetails) {
            setShouldLoadWorkflowDetails(false);
            getUnformattedWorkflowDetails(true, false);
        }
    }, []);

    const isDetailsReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnRPServices &&
            referenceData.axnCompanies &&
            referenceData.documentationLinks &&
            referenceData.axnServiceOfferingDataSources &&
            referenceData.fallbackServiceMappings &&
            referenceData.deployedPolicyComponents &&
            referenceData.axnAttributeTypeFields &&
            referenceData.simpleDeployedPolicyComponentConditions &&
            referenceData.simpleAXNWorkflows &&
            referenceData.axnAdminWorkflows &&
            referenceData.axnAcquiredAttributes &&
            referenceData.messagingTemplates &&
            referenceData.rpServiceTemplates &&
            referenceData.simpleAXNAdminRPServiceWorkflows &&
            referenceData.simpleAXNRPServiceWorkflows &&
            referenceData.axnAssertionAttributes &&
            referenceData.axnAvailablePropertyCategories &&
            referenceData.axnIdentityProviders &&
            referenceData.axnRelyingParties &&
            referenceData.axnAttributeTypes &&
            referenceData.axnAttributeLabels &&
            referenceData.attributeClassifications &&
            referenceData.apAssertions &&
            referenceData.axnAssertions &&
            referenceData.axnAvailableAssertionStatuses &&
            referenceData.availableAssertionScoreValues &&
            referenceData.availableTrustScoreValues &&
            referenceData.axnPropertyTypes &&
            referenceData.axnServiceOfferings &&
            referenceData.rpServiceAttributeProperties &&
            referenceData.rpServiceAttributePropertyMaps &&
            referenceData.serviceOfferingRefreshRates &&
            referenceData.serviceOfferingRefreshRateDefault &&
            referenceData.axnAssertionStatusMaps &&
            referenceData.assertionStatusDefaults &&
            referenceData.assertionScoreAssertionStatusValueMaps &&
            referenceData.trustScoreAssertionStatusValueMaps &&
            referenceData.scoreAttributeMaps &&
            referenceData.assertionScoreServiceOfferings &&
            referenceData.trustScoreServiceOfferings
        );
    }, [
        referenceData.axnRPServices,
        referenceData.axnCompanies,
        referenceData.documentationLinks,
        referenceData.axnServiceOfferingDataSources,
        referenceData.fallbackServiceMappings,
        referenceData.deployedPolicyComponents,
        referenceData.axnAttributeTypeFields,
        referenceData.simpleDeployedPolicyComponentConditions,
        referenceData.simpleAXNWorkflows,
        referenceData.axnAdminWorkflows,
        referenceData.axnAcquiredAttributes,
        referenceData.messagingTemplates,
        referenceData.simpleAXNAdminRPServiceWorkflows,
        referenceData.axnAssertionAttributes,
        referenceData.axnAvailablePropertyCategories,
        referenceData.axnIdentityProviders,
        referenceData.axnRelyingParties,
        referenceData.axnPropertyTypes,
        referenceData.axnServiceOfferings,
        referenceData.axnAttributeTypes,
        referenceData.axnAttributeLabels,
        referenceData.attributeClassifications,
        referenceData.apAssertions,
        referenceData.axnAssertions,
        referenceData.axnAvailableAssertionStatuses,
        referenceData.rpServiceTemplates,
        referenceData.simpleAXNRPServiceWorkflows,
        referenceData.availableAssertionScoreValues,
        referenceData.availableTrustScoreValues,
        referenceData.rpServiceAttributeProperties,
        referenceData.rpServiceAttributePropertyMaps,
        referenceData.serviceOfferingRefreshRates,
        referenceData.serviceOfferingRefreshRateDefault,
        referenceData.axnAssertionStatusMaps,
        referenceData.assertionStatusDefaults,
        referenceData.assertionScoreAssertionStatusValueMaps,
        referenceData.trustScoreAssertionStatusValueMaps,
        referenceData.scoreAttributeMaps,
        referenceData.assertionScoreServiceOfferings,
        referenceData.trustScoreServiceOfferings
    ]);

    const apAssertionStatusMaps: Array<ApAssertionStatusMap> = useMemo(() => {
        if (isDetailsReferenceDataPresent) {
            return getApAssertionStatusMaps(referenceData.apAssertions, referenceData.axnAssertionStatusMaps, referenceData.assertionStatusDefaults);
        }

        return [];
    }, [isDetailsReferenceDataPresent]);

    useEffect(() => {
        if (
            unformattedAdvancedWorkflowDetails &&
            isDetailsReferenceDataPresent &&
            !shouldLoadDetailsReferenceData &&
            !referenceData.isLoading.workflowDetails &&
            !shouldLoadWorkflowDetails
        ) {
            const services = formatRpServiceConfigurations(
                unformattedAdvancedWorkflowDetails.rpServices,
                referenceData.axnIdentityProviders,
                referenceData.axnRelyingParties,
                referenceData.axnAvailablePropertyCategories,
                referenceData.axnServiceOfferings,
                referenceData.axnServiceOfferingDataSources,
                referenceData.fallbackServiceMappings,
                referenceData.axnAttributeTypes,
                referenceData.axnAttributeLabels,
                referenceData.attributeClassifications,
                referenceData.apAssertions,
                referenceData.axnAssertions,
                referenceData.deployedPolicyComponents,
                referenceData.axnAttributeTypeFields,
                referenceData.rpServiceAttributeProperties,
                referenceData.rpServiceAttributePropertyMaps,
                apAssertionStatusMaps,
                referenceData.assertionScoreServiceOfferings,
                referenceData.trustScoreServiceOfferings,
                referenceData.axnCompanies
            );
            const formattedAdvancedWorkflowDetailsConfiguration = formatAdvancedWorkflowDetailsConfiguration(
                unformattedAdvancedWorkflowDetails,
                referenceData.axnRelyingParties,
                referenceData.axnCompanies,
                referenceData.axnAvailablePropertyCategories,
                services
            );

            dispatch(setAdvancedWorkflowDetails(formattedAdvancedWorkflowDetailsConfiguration, true));
        }
    }, [
        unformattedAdvancedWorkflowDetails,
        isDetailsReferenceDataPresent,
        shouldLoadDetailsReferenceData,
        referenceData.isLoading.workflowDetails,
        shouldLoadWorkflowDetails
    ]);

    const isDetailsDataPresent = useMemo(() => {
        return !!(isDetailsReferenceDataPresent && advancedWorkflowDetails && !shouldLoadWorkflowDetails);
    }, [isDetailsReferenceDataPresent, advancedWorkflowDetails, shouldLoadWorkflowDetails]);

    useEffect(() => {
        if (
            isDetailsDataPresent &&
            !shouldLoadDetailsReferenceData &&
            !referenceData.isLoading.workflowDetails
        ) {
            getMessagingTemplatesReduxRequest(dispatch)(
                referenceData.axnCompanies,
                referenceData.axnRelyingParties
            );
        }
    }, [
        isDetailsDataPresent,
        shouldLoadDetailsReferenceData,
        referenceData.isLoading.workflowDetails
    ]);

    const isLoading = useMemo(() => {
        return (
            isLoadingAdvancedWorkflowDetails ||
            referenceData.isLoading.workflowDetails ||
            shouldLoadWorkflowDetails ||
            shouldLoadDetailsReferenceData
        );
    }, [
        isLoadingAdvancedWorkflowDetails,
        referenceData.isLoading.workflowDetails,
        shouldLoadWorkflowDetails,
        shouldLoadDetailsReferenceData
    ]);

    const isLoaded = useMemo(() => {
        return !!(
            !isLoading &&
            advancedWorkflowDetails &&
            isDetailsReferenceDataPresent
        )
    }, [
        isLoading,
        advancedWorkflowDetails,
        isDetailsReferenceDataPresent
    ]);

    const fullAxnRpServices = useMemo(() => {
        if (isDetailsReferenceDataPresent && advancedWorkflowDetails?.rpServices) {
            return addNotDeployedWorkflowServicesToAxnRpServices(advancedWorkflowDetails.rpServices, referenceData.axnRPServices);
        }

        return [];

    }, [isDetailsReferenceDataPresent, referenceData.axnRPServices, advancedWorkflowDetails]);

    const handleSave = async (
        workflowDetailsConfiguration: FormattedWorkflowDetailsConfiguration,
        rpServiceConfigurations: Array<FormattedRpServiceConfiguration>
    ): Promise<FormattedAdvancedWorkflowDetailsConfiguration> => {
        const result = await updateWorkflowDetailsReduxRequest(dispatch)(
            workflowDetailsConfiguration,
            rpServiceConfigurations,
            referenceData.axnRelyingParties,
            referenceData.axnCompanies,
            referenceData.axnAvailablePropertyCategories,
            availableLanguages,
            referenceData.axnIdentityProviders,
            referenceData.axnAvailablePropertyCategories,
            referenceData.axnServiceOfferings,
            referenceData.axnServiceOfferingDataSources,
            referenceData.fallbackServiceMappings,
            referenceData.axnAttributeTypes,
            referenceData.axnAttributeLabels,
            referenceData.attributeClassifications,
            referenceData.apAssertions,
            referenceData.axnAssertions,
            referenceData.deployedPolicyComponents,
            referenceData.axnAttributeTypeFields,
            referenceData.rpServiceAttributeProperties,
            referenceData.rpServiceAttributePropertyMaps,
            apAssertionStatusMaps,
            referenceData.assertionScoreServiceOfferings,
            referenceData.trustScoreServiceOfferings
        );

        return result;
    };

    const handleUpdateChangeRequest = (changeRequest: WorkflowChangeRequest) => {
        if (changeRequest.changeRequestStatus === ChangeRequestStatusTypes.CANCELED) {
            getUnformattedWorkflowDetails(false, true);
        } else if (changeRequest.changeRequestStatus === ChangeRequestStatusTypes.DEPLOYED) {
            getUnformattedWorkflowDetails(false, true);
        } else {
            setWorkflowChangeRequests([changeRequest]);
            getCommonReferenceDataReduxRequest(dispatch, true)(
                [AxnReferenceDataTypes.SIMPLE_AXN_ADMIN_RP_SERVICE_WORKFLOW],
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.WORKFLOW_DETAILS
            );
        }
    }

    const selectedLanguageOption = useMemo(() => {
        return LANGUAGE_SELECT_OPTIONS.find(lso => lso.value === selectedLanguage);
    }, [selectedLanguage]);

    const onChangeSelectedLanguage = (newOption: SelectOption<LocaleTypes>) => {
        if (newOption) {
            setSelectedLanguage(newOption.value);
        }
    };

    const languageSelector = useMemo(() => {
        if (isLoaded && availableLanguages.length > 0) {
            const availableLanguageSelectOptions = LANGUAGE_SELECT_OPTIONS.filter(lso => {
                return availableLanguages.includes(lso.value);
            });
            return (
                <SelectFormGroup
                    className={styles.languageSelect}
                    ariaLabel={resources.pages.workflowDetails.inputs.language.label}
                    label={resources.pages.workflowDetails.inputs.language.label}
                    afterLabelTooltipText={resources.pages.workflowDetails.inputs.language.tooltip}
                    afterLabelTooltipTextPosition={"bottom"}
                    automationId={`workflow-details-language-input`}
                    controlId={`workflow-details-language-input`}
                    disabled={false}
                    isClearable={false}
                    isRequired={false}
                    isSearchable={true}
                    labelWidth={LabelWidthTypes.EXTRA_SMALL}
                    options={availableLanguageSelectOptions}
                    onChange={onChangeSelectedLanguage}
                    selectedOption={selectedLanguageOption}
                />
            );
        }

        return null;
    }, [isLoaded, selectedLanguage, availableLanguages]);

    return (
        <CardContainer>
            <ContentPageHeader
                title={resources.pages.workflowDetails.header}
                preHeaderButtonElement={
                    <ContentPageHeaderDocumentationLink
                        documentationLinks={referenceData.documentationLinks}
                        shortName={"workflow-details-page-header"}
                    />
                }
                headerButton={languageSelector}
            />
            <ContentPageBody isMaxWidth={true}>
                {isLoaded && (
                    <WorkflowDetailsComponent
                        selectedLanguage={selectedLanguage}
                        availableLanguages={availableLanguages}
                        setSelectedLanguage={setSelectedLanguage}
                        setAvailableLanguages={setAvailableLanguages}
                        axnDocumentationLinks={referenceData.documentationLinks}
                        axnCompanies={referenceData.axnCompanies}
                        axnRelyingParties={referenceData.axnRelyingParties}
                        axnAttributeTypeFields={referenceData.axnAttributeTypeFields}
                        attributeLabels={referenceData.axnAttributeLabels}
                        messagingTemplates={referenceData.messagingTemplates}
                        initialWorkflowDetailsConfiguration={advancedWorkflowDetails.workflow}
                        initialRpServiceConfigurations={advancedWorkflowDetails.rpServices}
                        shouldRefreshComponentState={shouldRefreshComponentState}
                        workflowChangeRequests={workflowChangeRequests}
                        rpServiceTemplates={referenceData.rpServiceTemplates}
                        axnRPServices={fullAxnRpServices}
                        axnRPServiceWorkflows={referenceData.simpleAXNRPServiceWorkflows}
                        axnAdminRPServiceWorkflows={referenceData.simpleAXNAdminRPServiceWorkflows}
                        userPermissions={session.userPermissions}
                        handleSave={handleSave}
                        setWorkflowChangeRequest={handleUpdateChangeRequest}
                        getFormattedRpServiceForNewService={getFormattedRpServiceForNewService}

                    />
                )}
                {(isLoading) && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};